import { useMemo, useState } from 'react';
import { UserCheck, Globe, Search, X, Check } from 'lucide-react';
import { AssignmentGroupsBar } from './AssignmentGroupsBar';

export const AssignUsersSection = ({ users, selected, onChange, dirty, api }) => {
  const [query, setQuery] = useState('');
  const byId = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? users.filter((u) => `${u.name} ${u.email}`.toLowerCase().includes(q)) : users;
  }, [users, query]);

  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  return (
    <div className="bg-card border border-border p-6" data-testid="assign-user-section">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h3 className="font-heading text-lg text-white uppercase tracking-wider flex items-center gap-2">
          <UserCheck className="h-5 w-5 text-primary" /> Assign to Users
        </h3>
        {selected.length ? (
          <span className="px-3 py-1 bg-blue-500/15 text-blue-300 text-xs border border-blue-500/30 rounded" data-testid="assign-count-badge">
            {selected.length} assigned
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-500/20 text-green-400 text-xs border border-green-500/30 rounded" data-testid="assign-public-badge">
            <Globe className="h-3 w-3" /> Public (All Users)
          </span>
        )}
      </div>
      <p className="text-muted-foreground text-sm mb-4">
        Pick one or more members. Only they will see this workout. Pick nobody to keep it public.
        {dirty && <span className="block text-primary mt-1" data-testid="assign-unsaved-hint">Unsaved: tap Save Changes to apply.</span>}
      </p>

      <AssignmentGroupsBar api={api} selected={selected} onChange={onChange} />

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="assign-chips">
          {selected.map((id) => (
            <span key={id} className="inline-flex items-center gap-1 pl-3 pr-1 py-1 rounded-full bg-primary/15 border border-primary/30 text-primary text-xs">
              {byId[id]?.name || 'Unknown user'}
              <button type="button" onClick={() => toggle(id)} className="p-0.5 rounded-full hover:bg-primary/20" aria-label="Remove" data-testid={`assign-chip-remove-${id}`}>
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <button type="button" onClick={() => onChange([])} className="text-xs text-red-400 hover:underline px-1" data-testid="assign-clear-all-btn">
            Clear all
          </button>
        </div>
      )}

      <div className="relative mb-2">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search members by name or email"
          className="w-full h-10 bg-black border border-border text-white pl-9 pr-3 rounded-md text-sm"
          data-testid="assign-user-search"
        />
      </div>
      <div className="max-h-64 overflow-y-auto rounded-md border border-border divide-y divide-border/60" data-testid="assign-user-list">
        {filtered.length === 0 && <p className="p-3 text-sm text-muted-foreground">No members match.</p>}
        {filtered.map((u) => {
          const on = selected.includes(u.id);
          return (
            <button
              type="button" key={u.id} onClick={() => toggle(u.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${on ? 'bg-primary/10' : 'hover:bg-white/5'}`}
              data-testid={`assign-user-option-${u.id}`}
            >
              <span className={`h-5 w-5 shrink-0 rounded border flex items-center justify-center ${on ? 'bg-primary border-primary text-black' : 'border-white/30'}`}>
                {on && <Check className="h-3.5 w-3.5" />}
              </span>
              <span className="min-w-0">
                <span className="block text-sm text-white truncate">{u.name}</span>
                <span className="block text-xs text-muted-foreground truncate">{u.email}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default AssignUsersSection;
